/**
 * `requestIdleCallback` is absent from the DOM lib types we build against, and from
 * Safari before 16.4 — so it is read off the window rather than called directly.
 */
type IdleWindow = Window & {
    requestIdleCallback?: (callback: () => void) => number
    cancelIdleCallback?: (handle: number) => void
}

/**
 * Defers `run` to the next browser idle period, for work that must not compete with
 * paint (CWV) — an analytics report, a silent session restore. Returns a cancel
 * function suitable as a `useEffect` cleanup, so a still-queued callback never fires
 * after the caller unmounts.
 *
 * Falls back to a near-immediate timeout where the API is unavailable: that still
 * yields to the current render tick, which is the point.
 */
export function runWhenIdle(run: () => void): () => void {
    const w = window as IdleWindow

    if (typeof w.requestIdleCallback === 'function') {
        const handle = w.requestIdleCallback(run)
        return () => w.cancelIdleCallback?.(handle)
    }

    const timer = setTimeout(run, 1)
    return () => {
        clearTimeout(timer)
    }
}
